import { useState } from "react";
import { Button, Combobox, FormField, Modal, TextInput } from "@a2z/ui";
import { useTranslate } from "@/lib/i18n";
import {
  VOID_REASON_NOTE_MAX_LENGTH,
  canConfirmVoid,
  canConfirmVoidReason,
  isVoidReasonNoteRequired,
  nextReasonCodeForQuery,
  reasonCodesForKind,
  voidReasonLabel,
  type ReasonCode,
  type ReasonKind,
} from "@/modules/pos/lib/voidReasons";
import {
  UnsavedChangesPrompt,
  useDiscardGuard,
} from "@/shared/UnsavedChangesPrompt";
import { ReturnToStockChoice } from "./ReturnToStockChoice";

/**
 * The confirm gate for a void, a comp or a remake.
 *
 * The first two take money off a bill, so neither happens on a single tap. The reason
 * code is required rather than optional, because the command log is the audit
 * trail and "the cashier voided something" is not an answer anyone can act on
 * later.
 *
 * This sheet used to ask a second question — "was it already made?" — which
 * decided whether the close wrote a waste row. It no longer exists (D20):
 * whether the kitchen cooked something is exactly whether it reached
 * PREPARING, which the depletion ledger already records at the moment it
 * happened. Asking a cashier to remember it was asking for a worse answer than
 * the system already had.
 *
 * D141 EXTENDS D20 — it does not bring the question back. The system still
 * decides *cooked* (the plate reached PREPARING, which the caller reads off
 * the plate's state); the cashier now decides only *where the cooked food
 * went*: in the bin (wasted — the default, and exactly today's behaviour) or
 * back on the shelf (returned to stock, which writes RETURNED_TO_STOCK rows at
 * the original cost). That second fact is one the ledger cannot know, so it is
 * the one thing worth asking — and it is asked only on a VOID with at least one
 * cooked plate selected. A comp has no choice (the server refuses the field on
 * a comp: comped food was eaten), a remake has none (that plate was binned),
 * and a plate still QUEUED has none (nothing was made).
 */
export function VoidItemDialog({
  kind,
  maxCount = 1,
  eligibleCountFor,
  onCancel,
  onConfirm,
}: {
  /**
   * D44 (B5): which decision is being filed — and therefore which reason
   * vocabulary the picker offers. Not cosmetic: the server keeps a closed
   * list per kind and refuses a code from the wrong one with a typed 400, so
   * a sheet that offered one list for all would refuse everything but
   * `OTHER`.
   */
  kind: ReasonKind;
  /**
   * How many identical plates the row holds. Above one the sheet asks "how
   * many" — two of three burgers coming back is ONE decision with one reason,
   * not three dialog rounds teaching the cashier to hate the till.
   */
  maxCount?: number;
  /**
   * D141 — how many of the plates a count of `count` would act on were
   * COOKED (PREPARING/READY/SERVED, by the till's knowledge; the server
   * re-checks). A function of the count because the caller owns which plates
   * a count selects (the newest N), and the sheet must not learn that rule a
   * second time. Omitted, or 0 for the chosen count, and no choice is shown.
   * Read only for `kind="void"`.
   */
  eligibleCountFor?: (count: number) => number;
  onCancel: () => void;
  /**
   * `returnToStock` is `true` only when the choice was SHOWN (a void with a
   * cooked plate selected) and the cashier picked "returned to stock"; every
   * other path — wasted, a comp, a remake, nothing cooked — reports `false`.
   */
  onConfirm: (
    reasonCode: string,
    count: number,
    reasonNote: string,
    returnToStock: boolean,
  ) => void | Promise<void>;
}) {
  const t = useTranslate();
  // DEF-7: no code is pre-selected. A pre-filled combobox reads as "already
  // answered" — the cashier types over it without noticing there was ever a
  // selection, and the stale pre-fill goes out on the command log instead of
  // whatever they meant. Starting `null` makes the picker honestly say
  // nothing has been chosen yet, and Confirm below refuses to fire until
  // something real has.
  const [reasonCode, setReasonCode] = useState<ReasonCode | null>(null);
  const isComp = kind === "comp";
  // B117/Q4. A remake costs no money, so it is not a destructive act and does
  // not wear the red — but it is the only record of WHY a plate was cooked
  // twice, and nobody can reconstruct it afterwards. Same sheet, third voice.
  const isRemake = kind === "remake";
  const [count, setCount] = useState(1);
  // D43 (86cbdhg4p): a separate field from the code picker above it, not a
  // replacement — the picker is where a code is CHOSEN, this is where what
  // actually happened gets written down. Required only once `OTHER` is
  // picked; `canConfirmVoid` is the single source of truth for that gate,
  // shared with the trimmed value Confirm sends.
  const [reasonNote, setReasonNote] = useState("");
  const noteRequired = isVoidReasonNoteRequired(reasonCode);
  // D141 — default wasted: an untouched sheet sends exactly today's command.
  const [returned, setReturned] = useState(false);
  const eligibleCount =
    kind === "void" && eligibleCountFor ? eligibleCountFor(count) : 0;
  const showReturnChoice = eligibleCount > 0;
  // Headless UI's Combobox only calls `onChange` when an option is actually
  // picked — typing never does, even when what's typed matches nothing. So a
  // selection can go stale silently: pick "Wrong order", then type over it,
  // and `reasonCode` would still read "WRONG_ORDER" with nothing on screen
  // backing it up. `nextReasonCodeForQuery` runs on every keystroke and
  // drops the selection the moment the typed text stops naming it, so the
  // invariant holds without waiting for a blur or a submit to notice.
  const handleReasonQueryChange = (query: string) => {
    setReasonCode((current) => nextReasonCodeForQuery(t, current, query));
  };
  /*
   * A reason chosen, a note typed, or a count moved off 1. Each is an answer
   * to "why did this plate come off the bill" — the one record nobody can
   * reconstruct afterwards — and a swipe must not take it silently.
   */
  const guard = useDiscardGuard(
    reasonCode !== null ||
      reasonNote.trim() !== "" ||
      count !== 1 ||
      (showReturnChoice && returned),
    onCancel,
  );
  return (
    <>
    {/* The platform Modal, not a hand-rolled sheet: `@a2z/ui`'s dialog is the
        one that carries the focus trap, the body scroll lock and
        backdrop-to-dismiss, and the app rule is that nothing rebuilds what the
        platform already has. An earlier version drew its own fixed overlay and
        Escape handler — and Tab could walk straight out of it onto the till
        behind, which is exactly the failure the shared component exists to
        prevent. */}
    <Modal
      isOpen
      onClose={guard.requestClose}
      size="sm"
      title={
        isRemake
          ? t((m) => m.pos.remake)
          : isComp
            ? t((m) => m.pos.comp)
            : t((m) => m.pos.void)
      }
      closeLabel={t((m) => m.common.close)}
      data-testid="void-dialog"
      footerActions={
        <>
          <Button variant="outline" size="touch" onClick={guard.requestClose}>
            {t((m) => m.common.cancel)}
          </Button>
          {/* Solid danger here, outline beside it: this IS the primary
              destructive action of the sheet, and the one place a filled red
              belongs. A remake is the exception — it takes nothing off the
              bill, and dressing "cook it again" in red would teach the floor
              to avoid the button that keeps the guest fed. */}
          <Button
            variant={isRemake ? "primary" : "danger"}
            size="touch"
            data-testid="void-confirm"
            disabled={!canConfirmVoid(reasonCode, reasonNote)}
            onClick={() => {
              if (!canConfirmVoid(reasonCode, reasonNote)) return;
              void onConfirm(
                reasonCode,
                count,
                reasonNote.trim(),
                showReturnChoice && returned,
              );
            }}
          >
            {t((m) => m.common.confirm)}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {maxCount > 1 ? (
          <div className="flex flex-col gap-1">
            <span className="text-xs font-bold text-[color:var(--text-muted)]">
              {t((m) => m.pos.voidCountLabel)}
            </span>
            <div className="flex flex-wrap gap-1.5">
              {Array.from({ length: maxCount }, (_, index) => index + 1).map(
                (option) => (
                  <button
                    key={option}
                    type="button"
                    aria-pressed={count === option}
                    data-testid={`void-count-${option}`}
                    onClick={() => setCount(option)}
                    className={[
                      "min-w-[44px] rounded-[var(--radius-control)] border px-3 py-2 text-sm font-extrabold tabular-nums transition-colors",
                      count === option
                        ? "border-[color:var(--danger)] bg-[color:var(--danger)] text-white"
                        : "border-[color:var(--border-strong)] text-[color:var(--text-secondary)] hover:text-[color:var(--text)]",
                    ].join(" ")}
                  >
                    {option}
                  </button>
                ),
              )}
            </div>
          </div>
        ) : null}
        {showReturnChoice ? (
          <ReturnToStockChoice
            returned={returned}
            onChange={setReturned}
            eligibleCount={eligibleCount}
            selectedCount={count}
            testIdPrefix="void"
          />
        ) : null}
        <FormField
          name="reasonCode"
          label={
            isRemake
              ? t((m) => m.pos.remakeReason)
              : isComp
                ? t((m) => m.pos.compReason)
                : t((m) => m.pos.voidReason)
          }
          // DEF-7 point 2: Confirm is disabled with no valid selection —
          // said here too, in the field's own voice, not just as a
          // disabled button a cashier has to guess the reason for.
          error={
            canConfirmVoidReason(reasonCode)
              ? undefined
              : isRemake
                ? t((m) => m.pos.remakeReasonRequired)
                : isComp
                  ? t((m) => m.pos.compReasonRequired)
                  : t((m) => m.pos.voidReasonRequired)
          }
        >
          <Combobox
            options={reasonCodesForKind(kind).map((code) => ({
              value: code,
              label: voidReasonLabel(t, code),
            }))}
            value={reasonCode}
            onChange={(next) => setReasonCode(next as ReasonCode | null)}
            onQueryChange={handleReasonQueryChange}
            // DEF-7 point 3: a placeholder, so the empty field reads as an
            // unanswered picker rather than inviting typed prose.
            placeholder={
              isRemake
                ? t((m) => m.pos.remakeReasonPlaceholder)
                : isComp
                  ? t((m) => m.pos.compReasonPlaceholder)
                  : t((m) => m.pos.voidReasonPlaceholder)
            }
            aria-label={
              isRemake
                ? t((m) => m.pos.remakeReason)
                : isComp
                  ? t((m) => m.pos.compReason)
                  : t((m) => m.pos.voidReason)
            }
            noResultsLabel={t((m) => m.common.empty)}
            // DEF-8: this combobox sits directly above the dialog's
            // Confirm/Cancel row, with no room below it for the options
            // panel before it would reach the footer. The panel is a
            // portal anchored to viewport edges — it has no notion of the
            // footer as a sibling to avoid, so left at the default it
            // portals straight over Confirm and eats the tap. Opening
            // upward, into the title/count area that already has room,
            // keeps the panel off the footer entirely instead of trying to
            // out-stack a portal that pointer-events would still hit.
            optionsPlacement="top"
          />
        </FormField>

        <FormField
          name="reasonNote"
          // D43: "Note" plus "(required)" when `OTHER` is picked — the
          // requirement is stated in the field's own label, not only
          // enforced by a disabled Confirm button, so a cashier who
          // switches to "Other" sees WHY the sheet stopped confirming
          // rather than having to guess.
          label={
            noteRequired
              ? `${t((m) => m.pos.voidReasonNoteLabel)} (${t((m) => m.common.required)})`
              : t((m) => m.pos.voidReasonNoteLabel)
          }
          error={
            noteRequired && reasonNote.trim().length === 0
              ? t((m) => m.pos.voidReasonNoteRequired)
              : undefined
          }
        >
          <TextInput
            name="reasonNote"
            value={reasonNote}
            onChange={(event) => setReasonNote(event.target.value)}
            placeholder={
              noteRequired
                ? t((m) => m.pos.voidReasonNoteRequiredPlaceholder)
                : t((m) => m.pos.voidReasonNoteOptionalPlaceholder)
            }
            maxLength={VOID_REASON_NOTE_MAX_LENGTH}
            dir="auto"
            data-testid="void-reason-note"
          />
        </FormField>

      </div>
    </Modal>
    <UnsavedChangesPrompt
      isOpen={guard.asking}
      onDiscard={guard.discard}
      onKeepEditing={guard.keepEditing}
      testId="void-discard-prompt"
    />
    </>
  );
}
